import React from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { IconDefinitionConstants, InvoiceConstants } from "utils/constants";
import { TranslationKeys } from "translations";
import { DimensionEnums, StyleEnums } from "models/enums";
import { InvoiceProductTabs } from "models/enums/invoice";
import { InvoiceContentSelectionFormType, InvoiceProductRowProps } from "models/propTypes/invoicePropTypes";
import { InvoiceFormTypes } from "models/formTypes";
import { WintFormKey, BaseFormFieldProps } from "models/propTypes/wintFormPropsTypes";
//#endregion
//#region components
import {
  TextInput,
  SearchInput,
  CurrencyInput,
  EditableTableTab,
  MasterRowSummary,
  NumberInput,
  SelectInput,
  DimensionInputSelector,
  unconnectedFormField,
} from "components/common/wintForms";
import { TableRow, TableCell } from "@mui/material";
import { Icon, Periodisation, RoundButton, SimpleTooltip } from "components/common";
import RecurringPeriodisation from "./recurringPeriodisation";
import { useInvoiceService } from "serviceHooks";
import { getIn, setIn } from "formik";
//#endregion
import { InvoiceHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const InvoiceProductRow: React.FC<InvoiceProductRowProps<InvoiceContentSelectionFormType>> = (props) => {
  const invoiceService = useInvoiceService();
  const {
    row,
    formValues,
    touched,
    errors,
    index,
    updateRow,
    removeRow,
    connectToForm,
    onArticleSearch,
    onArticleSelect,
    onUnitSelect,
    suggestedArticles,
    units,
    currentTab,
    isLastRow,
    isQuotation,
    isRecurring,
    isEUCustomer,
    vatLockedToVatFree,
    masterRow,
    masterRowDimensions,
    masterRowPeriodisation,
    masterRowRecurringPeriodisation,
    rowLevelDimensions,
    rowLevelPeriodisation,
    disablePeriodisationModeToggle,
    canDrag,
    isDragging,
    isOver,
    handlerId,
    dragDropRef,
  } = props;

  const rowName = `products[${index}]`;

  const sum = invoiceService.calculateAmount(row.price ?? 0, row.quantity);

  const setRowField = (name: WintFormKey<InvoiceFormTypes.Product>, value: any, shouldValidate?: boolean) => {
    const newRow = setIn(row, name, value);
    updateRow(newRow, shouldValidate);
  };

  const handleChange = (name: WintFormKey<InvoiceFormTypes.Product>) => (e: React.ChangeEvent<any>) => {
    setRowField(name, e.target.value);
  };

  const connectRowToForm = (name: WintFormKey<InvoiceFormTypes.Product>): BaseFormFieldProps => {
    const connector = connectToForm((rowName + "." + name) as WintFormKey<InvoiceContentSelectionFormType>);

    return {
      name: connector.name,
      value: connector.value,
      onChange: handleChange(name),
      onBlur: (e: React.FocusEvent<any>) => {
        if (isLastRow || formValues.products?.length === undefined) return;
        connector.onBlur(e);
        connector.validate();
      },
      setValue: (value: any, shouldValidate?: boolean) => setRowField(name, value, shouldValidate),
      setTouched: (isTouched?: boolean, shouldValidate?: boolean) => connector.setTouched(isTouched, shouldValidate),
      touched: !!getIn(touched, name),
      error: getIn(errors, name),
      hasError: !!getIn(touched, name) && !!getIn(errors, name),
    };
  };

  const removeRowEvent = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    removeRow(index);
  };

  const onRowClick = React.useCallback(() => {
    if (isLastRow) {
      updateRow({ ...row }, index === 0);
    }
  }, [isLastRow]);

  const euVatCategories = React.useMemo(
    () => invoiceService.getEuVatCategoryTypes().map((c) => ({ value: c.id!, label: c.value })),
    []
  );

  const unitProps = connectRowToForm("unit");
  const quantityProps = connectRowToForm("quantity");

  // VAT is locked to the article's own rate for domestic customers on rows whose selected article
  // has a configured revenue account ("Gäller endast rader vars artikel har konto").
  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);
  const isVatLocked = InvoiceHelper.isRowVatLocked(
    row,
    formValues.isDomesticCustomer,
    props.revenueAccountsEnabled,
    noVatCompany
  );
  const vatLockedDescriptionId = `${rowName}-vat-locked-description`;
  const vatLockedTooltipText = LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.VatLockedTooltip);

  return (
    <TableRow
      className={clsx(cssExports.fieldRow, isLastRow ? cssExports.tableLastRow : "")}
      onClick={onRowClick}
      ref={canDrag ? dragDropRef : undefined}
      style={canDrag ? { opacity: isDragging ? 0.5 : 1, backgroundColor: isOver ? "#fbf9f8" : undefined } : undefined}
      data-handler-id={canDrag ? handlerId : undefined}
    >
      {canDrag ? (
        <TableCell>
          <div style={{ cursor: "move" }}>
            <Icon icon={IconDefinitionConstants.WListDrag} className={cssExports.listDragIcon} />
          </div>
        </TableCell>
      ) : (
        <TableCell className={cssExports.tableLastRowNotDraggable}></TableCell>
      )}

      <TableCell className={cssExports.article}>
        <SearchInput
          {...connectRowToForm("article")}
          setValue={onArticleSelect}
          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Product)}
          placeholder={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SearchProduct)}
          items={onArticleSearch}
          suggestedItems={suggestedArticles}
          freeSolo
          className={cssExports.info}
          popperMinWidth={100}
        />
      </TableCell>

      {(() => {
        switch (currentTab) {
          case InvoiceProductTabs.Amount:
            return (
              <EditableTableTab>
                <TableCell style={{ width: "22%" }}>
                  <NumberInput
                    {...quantityProps}
                    label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Quantity)}
                    allowLeadingZeros={false}
                    decimalPoints={2}
                    disabled={!row.unit}
                  />
                </TableCell>
                <TableCell style={{ width: "20%" }}>
                  <SelectInput
                    {...unitProps}
                    setValue={onUnitSelect}
                    value={unitProps.value ?? 0}
                    label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Unit)}
                    items={units}
                  />
                </TableCell>
                <TableCell style={{ width: "18%" }}>
                  <CurrencyInput
                    {...connectRowToForm("price")}
                    label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.Price)}
                  />
                </TableCell>
                <TableCell style={{ width: "20%" }}>
                  {vatLockedToVatFree ? (
                    <SimpleTooltip
                      text={LocalizationHelperInstance.getFormattedMessage(
                        TranslationKeys.Common.VatRateLockedNotVatRegistered
                      )}
                    >
                      <div>
                        <TextInput
                          {...unconnectedFormField}
                          name="tax"
                          disabled
                          value={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree)}
                          label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.VAT)}
                        />
                      </div>
                    </SimpleTooltip>
                  ) : (
                    <>
                      {isVatLocked && (
                        <span id={vatLockedDescriptionId} className={cssExports.visuallyHidden}>
                          {vatLockedTooltipText}
                        </span>
                      )}
                      <SimpleTooltip text={isVatLocked ? vatLockedTooltipText : undefined}>
                        <div>
                          <SelectInput
                            {...connectRowToForm("tax")}
                            label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.VAT)}
                            items={InvoiceConstants.VatTypes.map((vat) => ({
                              value: vat,
                              label:
                                vat === 0
                                  ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.VATFree)
                                  : `${vat}%`,
                            }))}
                            readonly={isVatLocked}
                            readonlyStyle={isVatLocked ? "disabled" : undefined}
                            hideDropdownIcon={isVatLocked}
                            ariaDescribedBy={isVatLocked ? vatLockedDescriptionId : undefined}
                          />
                        </div>
                      </SimpleTooltip>
                    </>
                  )}
                </TableCell>
                <TableCell style={{ width: "20%" }}>
                  <CurrencyInput
                    {...unconnectedFormField}
                    readonly
                    value={sum}
                    rightAlign
                    label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.TotalPrice)}
                  />
                </TableCell>
              </EditableTableTab>
            );
          case InvoiceProductTabs.Text:
            return (
              <EditableTableTab>
                <TableCell>
                  <TextInput
                    {...connectRowToForm("text")}
                    label={LocalizationHelperInstance.getFormattedMessage(
                      isQuotation ? TranslationKeys.Quotation.QuotationText : TranslationKeys.Invoice.InvoiceText
                    )}
                    optional
                    disabled={row.article && (!row.article.id || row.article.id === -1)}
                  />
                </TableCell>
              </EditableTableTab>
            );
          case InvoiceProductTabs.Periodisation:
            return (
              <EditableTableTab>
                <TableCell>
                  {isRecurring && "recurringSettings" in formValues ? (
                    rowLevelPeriodisation || masterRow ? (
                      <RecurringPeriodisation
                        {...connectRowToForm("recurringPeriodisation")}
                        isPeriodisationAdvancedConfiguration={rowLevelPeriodisation}
                        recurringInvoiceStartDate={formValues.recurringSettings.recurringInvoiceStartDate}
                        recurringInvoiceEndDate={formValues.recurringSettings.recurringInvoiceEndDate}
                        recurringInvoiceFrequency={formValues.recurringSettings.recurringInvoiceFrequency}
                      />
                    ) : (
                      <MasterRowSummary
                        entityName={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accrual)}
                      >
                        {masterRowRecurringPeriodisation}
                      </MasterRowSummary>
                    )
                  ) : (
                    <>
                      {rowLevelPeriodisation || masterRow ? (
                        <Periodisation
                          {...connectRowToForm("periodisation")}
                          fromRecurring={formValues.fromRecurring}
                          disableModeToggle={disablePeriodisationModeToggle}
                        />
                      ) : (
                        <MasterRowSummary
                          entityName={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Accrual)}
                        >
                          {masterRowPeriodisation}
                        </MasterRowSummary>
                      )}
                    </>
                  )}
                </TableCell>
              </EditableTableTab>
            );
          case InvoiceProductTabs.Dimensions:
            return (
              <EditableTableTab>
                <TableCell>
                  {rowLevelDimensions || masterRow ? (
                    <DimensionInputSelector
                      {...connectRowToForm("dimensions")}
                      type={DimensionEnums.DimensionCategory.Invoice}
                      allowedTypes={isQuotation ? invoiceService.getCompatibleDimensionTypes(isQuotation) : undefined}
                    />
                  ) : (
                    <MasterRowSummary
                      entityName={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Dimensions)}
                    >
                      {masterRowDimensions}
                    </MasterRowSummary>
                  )}
                </TableCell>
              </EditableTableTab>
            );
          case InvoiceProductTabs.EUVatCategory:
            return (
              <EditableTableTab>
                <TableCell>
                  <SelectInput
                    {...connectRowToForm("eUVatCategoryId")}
                    label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.EUVatCategory)}
                    items={euVatCategories}
                    optional={!isEUCustomer}
                  />
                </TableCell>
              </EditableTableTab>
            );
        }
      })()}
      <TableCell className={cssExports.editableTableActionButton}>
        {!isLastRow && (
          <RoundButton
            size="xs"
            displayType={StyleEnums.DisplayType.None}
            className={cssExports.close}
            onClick={removeRowEvent}
          >
            <Icon icon={IconDefinitionConstants.WTimes} />
          </RoundButton>
        )}
      </TableCell>
    </TableRow>
  );
};

export default InvoiceProductRow;
